/**
 * Return URL Service
 * Handles storage and retrieval of return URLs for post-authentication redirection
 * Works cross-platform: web (localStorage) and native (AsyncStorage)
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { validateReturnUrl, sanitizeReturnUrl } from '../utils/urlValidation';

const RETURN_URL_KEY = 'vertiv_return_url';

/**
 * Return URL Service
 * Provides cross-platform storage for preserving user's intended destination
 * during authentication flows
 */
class ReturnUrlService {
  /**
   * Saves a return URL for post-authentication redirect
   * @param url - The URL to return to after authentication
   * @returns Promise<boolean> - true if saved successfully, false otherwise
   */
  async saveReturnUrl(url: string): Promise<boolean> {
    try {
      // Validate and sanitize the URL
      const sanitizedUrl = sanitizeReturnUrl(url);
      if (!sanitizedUrl) {
        console.warn('⚠️ ReturnUrlService: Invalid return URL, not saving:', url);
        return false;
      }

      console.log('💾 ReturnUrlService: Saving return URL:', sanitizedUrl);

      // Use platform-appropriate storage
      if (Platform.OS === 'web') {
        // Web: Use localStorage
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.setItem(RETURN_URL_KEY, sanitizedUrl);
          return true;
        } else {
          console.error('❌ ReturnUrlService: localStorage not available');
          return false;
        }
      } else {
        // Native: Use AsyncStorage
        await AsyncStorage.setItem(RETURN_URL_KEY, sanitizedUrl);
        return true;
      }
    } catch (error) {
      console.error('❌ ReturnUrlService: Failed to save return URL:', error);
      return false;
    }
  }

  /**
   * Retrieves the saved return URL
   * @returns Promise<string | null> - The saved URL or null if not found/invalid
   */
  async getReturnUrl(): Promise<string | null> {
    try {
      let returnUrl: string | null = null;

      // Retrieve from platform-appropriate storage
      if (Platform.OS === 'web') {
        // Web: Use localStorage
        if (typeof window !== 'undefined' && window.localStorage) {
          returnUrl = localStorage.getItem(RETURN_URL_KEY);
        }
      } else {
        // Native: Use AsyncStorage
        returnUrl = await AsyncStorage.getItem(RETURN_URL_KEY);
      }

      if (!returnUrl) {
        console.log('ℹ️ ReturnUrlService: No return URL found');
        return null;
      }

      // Validate the retrieved URL
      if (!validateReturnUrl(returnUrl)) {
        console.warn('⚠️ ReturnUrlService: Stored return URL is invalid, clearing:', returnUrl);
        await this.clearReturnUrl();
        return null;
      }

      console.log('📖 ReturnUrlService: Retrieved return URL:', returnUrl);
      return returnUrl;

    } catch (error) {
      console.error('❌ ReturnUrlService: Failed to get return URL:', error);
      return null;
    }
  }

  /**
   * Clears the saved return URL
   * Should be called after successfully redirecting or when URL is no longer needed
   * @returns Promise<boolean> - true if cleared successfully
   */
  async clearReturnUrl(): Promise<boolean> {
    try {
      console.log('🗑️ ReturnUrlService: Clearing return URL');

      if (Platform.OS === 'web') {
        // Web: Use localStorage
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.removeItem(RETURN_URL_KEY);
          return true;
        }
      } else {
        // Native: Use AsyncStorage
        await AsyncStorage.removeItem(RETURN_URL_KEY);
        return true;
      }

      return false;
    } catch (error) {
      console.error('❌ ReturnUrlService: Failed to clear return URL:', error);
      return false;
    }
  }

  /**
   * Checks if a return URL is currently stored
   * @returns Promise<boolean> - true if a return URL exists
   */
  async hasReturnUrl(): Promise<boolean> {
    try {
      if (Platform.OS === 'web') {
        if (typeof window !== 'undefined' && window.localStorage) {
          return localStorage.getItem(RETURN_URL_KEY) !== null;
        }
        return false;
      } else {
        const url = await AsyncStorage.getItem(RETURN_URL_KEY);
        return url !== null;
      }
    } catch (error) {
      console.error('❌ ReturnUrlService: Failed to check return URL:', error);
      return false;
    }
  }

  /**
   * Validates a URL without storing it
   * Useful for checking URLs before saving
   * @param url - The URL to validate
   * @returns boolean - true if URL is valid and safe
   */
  validateUrl(url: string): boolean {
    return validateReturnUrl(url);
  }
}

// Export singleton instance
export const returnUrlService = new ReturnUrlService();
